import { Building01, SearchLg } from "@untitledui/icons";
import { useMemo, useState } from "react";

import { Avatar } from "@/components/ui/avatar/avatar";
import { getInitials } from "@/components/ui/avatar/utils";
import { RadioButtonBase } from "@/components/ui/radio-buttons/radio-buttons";
import { useOrgSelection } from "@/context/use-org-selection";
import { cx } from "@/utils/cx";

/**
 * Searchable org list used by both the sidebar org card and the account menu.
 */
export const OrgSwitchPanel = ({ onClose }: { onClose?: () => void }) => {
	const { organizations, selected, selectOrg } = useOrgSelection();
	const [orgQuery, setOrgQuery] = useState("");

	const filteredOrganizations = useMemo(() => {
		const query = orgQuery.trim().toLowerCase();
		const matched = query
			? organizations.filter(
					(org) =>
						org.name.toLowerCase().includes(query) ||
						org.orgCode.toLowerCase().includes(query),
				)
			: organizations;

		const selectedCode = selected?.orgCode;
		if (!selectedCode) return matched;

		return [...matched].sort((a, b) => {
			if (a.orgCode === selectedCode) return -1;
			if (b.orgCode === selectedCode) return 1;
			return 0;
		});
	}, [orgQuery, organizations, selected?.orgCode]);

	return (
		<div className="flex flex-col">
			<p className="px-3.5 pt-1.5 pb-1 text-xs font-semibold text-tertiary">
				Switch organization
			</p>

			{organizations.length > 1 && (
				<div className="px-1.5 pb-1.5">
					<label className="relative flex items-center rounded-lg bg-primary ring-1 ring-primary ring-inset focus-within:ring-2 focus-within:ring-brand">
						<span className="sr-only">Search organizations</span>
						<SearchLg
							aria-hidden="true"
							className="pointer-events-none absolute left-3 size-4 text-fg-quaternary"
						/>
						<input
							autoComplete="off"
							className="w-full bg-transparent py-2 pr-3 pl-9 text-md text-primary outline-hidden placeholder:text-placeholder"
							onChange={(event) => setOrgQuery(event.target.value)}
							placeholder="Search"
							type="search"
							value={orgQuery}
						/>
					</label>
				</div>
			)}

			<div className="max-h-[235px] overflow-y-auto overscroll-contain">
				{filteredOrganizations.length === 0 ? (
					<p className="px-3.5 py-2 text-sm text-tertiary">
						No organizations match.
					</p>
				) : (
					filteredOrganizations.map((org) => {
						const isSelected = selected?.orgCode === org.orgCode;
						return (
							<button
								className="group/item w-full cursor-pointer px-1.5 focus:outline-hidden"
								key={org.orgCode}
								onClick={() => {
									selectOrg(org.orgCode);
									onClose?.();
								}}
								type="button"
							>
								<div
									className={cx(
										"flex w-full items-center gap-3 rounded-md p-2 group-hover/item:bg-primary_hover",
										"outline-focus-ring group-focus-visible/item:outline-2 group-focus-visible/item:outline-offset-2",
									)}
								>
									<Avatar
										initials={getInitials(org.name)}
										placeholderIcon={Building01}
										size="md"
									/>
									<div className="min-w-0 flex-1 text-left">
										<p className="truncate text-sm font-semibold text-primary">
											{org.name}
										</p>
										<p className="truncate text-sm text-tertiary">
											{org.orgCode}
										</p>
									</div>
									<RadioButtonBase isSelected={isSelected} size="md" />
								</div>
							</button>
						);
					})
				)}
			</div>
		</div>
	);
};
